import { useCallback, useEffect, useState } from 'react';
import { Layer, Marker, Popup, Source, useMap } from 'react-map-gl';
import { trackEvent, MAP_DISTANCE_MEASURE } from '../../utils/googleAnalytics';

// Haversine distance calculation
const calculateDistance = (point1, point2) => {
  const R = 6371; // Earth's radius in km
  const dLat = ((point2.lat - point1.lat) * Math.PI) / 180;
  const dLon = ((point2.lng - point1.lng) * Math.PI) / 180;
  const a =
    Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.cos((point1.lat * Math.PI) / 180) *
      Math.cos((point2.lat * Math.PI) / 180) *
      Math.sin(dLon / 2) *
      Math.sin(dLon / 2);
  const distanceInKm = R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  const distanceInMiles = distanceInKm * 0.621371;
  return {
    miles: distanceInMiles.toFixed(2),
    km: distanceInKm.toFixed(2),
  };
};

export const DistanceMeasureTool = ({ isActive, onMeasure }) => {
  const { current: map } = useMap();
  const [points, setPoints] = useState([]);
  const [distance, setDistance] = useState(null);

  // Reset when tool is deactivated
  useEffect(() => {
    if (!isActive) {
      setPoints([]);
      setDistance(null);
    }
  }, [isActive]);

  const handleDblClick = useCallback(
    e => {
      if (!isActive) return;
      // Prevent the map's default double-click zoom behavior
      e.preventDefault();

      const { lng, lat } = e.lngLat;
      const newPoint = { lng, lat };

      setPoints(prev => {
        const newPoints = [...prev, newPoint];

        if (newPoints.length === 2) {
          const dist = calculateDistance(newPoints[0], newPoints[1]);
          setDistance(dist);

          trackEvent(MAP_DISTANCE_MEASURE, { distance: dist, points: newPoints });

          if (onMeasure) onMeasure(dist);
          return newPoints;
        }
        // Reset if somehow more than 2 points accumulate
        if (newPoints.length > 2) {
          setDistance(null);
          return [newPoint];
        }
        return newPoints;
      });
    },
    [isActive, onMeasure],
  );

  // Attach/detach dblclick listener on the map instance directly
  useEffect(() => {
    if (!map) return;
    map.on('dblclick', handleDblClick);
    return () => map.off('dblclick', handleDblClick);
  }, [map, handleDblClick]);

  const handleReset = () => {
    setPoints([]);
    setDistance(null);
  };

  // Build a GeoJSON LineString from the two points for the dashed line —
  const lineGeoJSON =
    points.length === 2
      ? {
          type: 'Feature',
          geometry: {
            type: 'LineString',
            coordinates: points.map(p => [p.lng, p.lat]),
          },
        }
      : null;

  return (
    <>
      {points.map((point, idx) => (
        <Marker key={idx} longitude={point.lng} latitude={point.lat} style={{ cursor: 'default' }}>
          <div className="h-3 w-3 rounded-full border-2 border-white bg-blue-500 shadow-md" />
        </Marker>
      ))}

      {lineGeoJSON && (
        <Source id="measure-line" type="geojson" data={lineGeoJSON}>
          <Layer
            id="measure-line-layer"
            type="line"
            paint={{
              'line-color': '#3B82F6',
              'line-width': 3,
              'line-dasharray': [2, 2],
            }}
          />
        </Source>
      )}

      {points.length === 2 && distance && (
        <Popup
          longitude={points[1].lng}
          latitude={points[1].lat}
          closeButton={false}
          closeOnClick={false}
          anchor="bottom"
          offset={16}
        >
          <div className="flex flex-col text-sm">
            <div className="flex-inline flex gap-2">
              <p className="my-1 font-bold">Distance:</p>
              <p className="my-1">{`${distance.miles} miles (${distance.km} km)`}</p>
            </div>
            <button
              data-export-exclude="true"
              id="clear-distance"
              onClick={handleReset}
              className="mt-2 self-center rounded bg-slateBlue px-2 py-1 text-xs text-white hover:bg-slateBlue/80"
            >
              Clear
            </button>
          </div>
        </Popup>
      )}
    </>
  );
};
